import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FormHandles } from '@unform/core';
import { addDays, format } from 'date-fns';
import api from 'services/api';
import * as Yup from 'yup';

import { Container, Content, Form } from './styles';
import Checkbox from 'components/Checkbox';
import DatePicker from 'components/DatePicker';
import Tabs, { Tab } from 'components/Tabs';
import Radio from 'components/Radio';
import Button from 'components/Button';
import Switch from 'components/Switch';

import { toast } from 'react-toastify';
import { FiDownload } from 'react-icons/fi';
import AppError from 'utils/AppError';
import { Emissora } from 'pages/@types';
import getValidationErrors from 'services/getValidationErros';

interface IOption {
  id: string;
  label: string;
}

// configuração do validator
const schema = Yup.object().shape({
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de fim inválida')
    .min(Yup.ref('datainicio'), 'Data Fim precisa ser posterior a Data Início'),
  emissora: Yup.string().required('Escolha pelo menos uma emissora'),
});

const schemaChamadas = Yup.object().shape({
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de fim inválida')
    .min(Yup.ref('datainicio'), 'Data Fim precisa ser posterior a Data Início'),
  setor: Yup.string()
    .nullable()
    .required('Campo Obrigatório'),
});

const schemaSpotRadio = Yup.object().shape({
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de fim inválida')
    .min(Yup.ref('datainicio'), 'Data Fim precisa ser posterior a Data Início'),
});
const schemaOptativosLocais = Yup.object().shape({
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data de fim inválida')
    .min(Yup.ref('datainicio'), 'Data Fim precisa ser posterior a Data Início'),
});
export default function Relatorio() {
  /*
   * STATES
   */
  const [emissoras, setEmissoras] = useState<IOption[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const [rede, setRede] = useState(false);
  /*
   * REF
   */
  const formRef = useRef<FormHandles>(null);
  const formRefChamadas = useRef<FormHandles>(null);
  const formRefOptativos = useRef<FormHandles>(null);

  const download = useCallback(async (response, filename) => {
    const elink = document.createElement('a');
    const virtualUrl = window.URL.createObjectURL(response.data);
    elink.href = virtualUrl;
    elink.download = filename;
    elink.click();
  }, []);

  const handleRede = useCallback(
    async e => {
      e.preventDefault();
      if (formRef.current)
        if (formRef.current.getFieldValue('emissora').length === 8) {
          setRede(false);
          formRef.current.clearField('emissora');
        } else {
          setRede(true);
          formRef.current.setFieldValue(
            'emissora',
            emissoras.map(emi => emi.id).join(),
          );
        }
    },
    [emissoras],
  );

  const handleDownloadRelatorioComerciais = useCallback(
    async data => {
      try {
        if (formRef.current) {
          setLoadingDownload(true);
          formRef.current.setErrors({});
          await schema.validate({ ...data }, { abortEarly: false });
          const response = await api.get('/comerciaists/relatorio', {
            timeout: 360000,
            headers: { 'Content-Type': 'application/octet-stream' },
            responseType: 'blob',
            params: {
              emissoras: data.emissora.join(),
              datainicio: format(data.datainicio, 'yyyy-MM-dd'),
              datafim: format(addDays(data.datafim, 1), 'yyyy-MM-dd'),
              filtrarDuplicados: data.filtrarDuplicados,
              filtrarIndisponiveis: data.filtrarIndisponiveis,
              filtrarPoliticos: data.filtrarPoliticos,
            },
          });

          await download(response, 'Relatório Comerciais ADX.xlsx');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingDownload(false);
    },
    [download],
  );

  const handleDownloadRelatorioChamadas = useCallback(
    async data => {
      try {
        if (formRefChamadas.current) {
          setLoadingDownload(true);
          formRefChamadas.current.setErrors({});
          await schemaChamadas.validate({ ...data }, { abortEarly: false });
          const response = await api.get('/chamadasts/relatorio', {
            timeout: 360000,
            headers: { 'Content-Type': 'application/octet-stream' },
            responseType: 'blob',
            params: {
              setor: data.setor,
              datainicio: format(data.datainicio, 'yyyy-MM-dd'),
              datafim: format(data.datafim, 'yyyy-MM-dd'),
            },
          });
          await download(response, 'Relatório Produções ADX.xlsx');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRefChamadas.current)
            formRefChamadas.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingDownload(false);
    },
    [download],
  );
  const handleDownloadRelatorioSpotRadio = useCallback(async () => {
    try {
      if (formRefChamadas.current) {
        const data = formRefChamadas.current.getData();
        setLoadingDownload(true);
        formRefChamadas.current.setErrors({});
        await schemaSpotRadio.validate({ ...data }, { abortEarly: false });

        const response = await api.get('/spotradiorelatorio/relatorio', {
          timeout: 360000,
          headers: { 'Content-Type': 'application/octet-stream' },
          responseType: 'blob',
          params: {
            datainicio: format(data.datainicio, 'yyyy-MM-dd'),
            datafim: format(addDays(data.datafim, 1), 'yyyy-MM-dd'),
          },
        });

        await download(response, 'Relatório Spots ADX.xlsx');
      }
    } catch (err) {
      console.log(err);
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        if (formRefChamadas.current) formRefChamadas.current.setErrors(errors);
        toast.error(err.message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        toast.error(appError.message);
      }
    }
    setLoadingDownload(false);
  }, [download]);

  const handleDownloadRelatorioOptativosLocais = useCallback(
    async data => {
      try {
        if (formRefOptativos.current) {
          setLoadingDownload(true);
          formRefOptativos.current.setErrors({});
          await schemaOptativosLocais.validate(
            { ...data },
            { abortEarly: false },
          );
          const response = await api.get('/optativolocal/relatorio', {
            timeout: 360000,
            headers: { 'Content-Type': 'application/octet-stream' },
            responseType: 'blob',
            params: {
              datainicio: format(data.datainicio, 'yyyy-MM-dd'),
              datafim: format(addDays(data.datafim, 1), 'yyyy-MM-dd'),
              filtrarDeletados: data.filtrarDeletados,
              contemMidia: data.contemMidia,
            },
          });
          await download(response, 'Relatório Optativos Locais ADX.xlsx');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRefOptativos.current)
            formRefOptativos.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingDownload(false);
    },
    [download],
  );

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadEmissoras() {
      await api
        .get('/emissoras')
        .then(response => {
          if (response && response.data) {
            const emissorasNormalized = response.data.map((emi: Emissora) => ({
              id: emi.id,
              label: emi.nome,
            }));
            setEmissoras(emissorasNormalized);
          }
        })
        .catch(error => {
          if (error instanceof Error) {
            toast.error(error.message);
          } else console.log(error);
        });
    }

    loadEmissoras();
  }, []);

  return (
    <Container>
      <Tabs>
        <Tab
          title="RELATÓRIO DE COMERCIAIS"
          id={1}
          height="calc(100vh - 130px)"
          checked={activeTabIndex === 1}
          onChange={() => setActiveTabIndex(1)}
        >
          <Content>
            <Form
              ref={formRef}
              onSubmit={handleDownloadRelatorioComerciais}
              autoComplete="off"
            >
              <div>
                <div className="datas">
                  <div>
                    <h5>Período:</h5>
                    <div style={{ display: 'flex' }}>
                      <DatePicker
                        placeholderText="DE"
                        name="datainicio"
                        disabled={loadingDownload}
                      />
                      <DatePicker
                        placeholderText="ATÉ"
                        name="datafim"
                        disabled={loadingDownload}
                      />
                    </div>
                  </div>
                  <div className="switchs">
                    <div>
                      <h5>Filtrar CMs Duplicados:</h5>
                      <Switch name="filtrarDuplicados" />
                    </div>
                    <div>
                      <h5>Filtrar Mídias Indisponíveis:</h5>
                      <Switch name="filtrarIndisponiveis" />
                    </div>
                    <div>
                      <h5>Filtrar Políticos:</h5>
                      <Switch name="filtrarPoliticos" />
                    </div>
                  </div>
                </div>
                <div>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <h5>Exibidoras:</h5>
                    <Button
                      fill
                      color="primary"
                      size="xsm"
                      onClick={e => handleRede(e)}
                    >
                      REDE ? {rede ? 'SIM' : 'NÃO'}
                    </Button>
                  </div>
                  <div>
                    <Checkbox
                      name="emissora"
                      options={emissoras}
                      disabled={loadingDownload}
                      handleSelected={() =>
                        setRede(
                          formRef.current?.getFieldValue('emissoras').length ===
                            8,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="botao">
                <Button
                  type="submit"
                  color="primary"
                  disabled={loadingDownload}
                  style={{ width: '20rem' }}
                >
                  {loadingDownload ? (
                    'Gerando o relatório'
                  ) : (
                    <>
                      <FiDownload /> Gerar relatório
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Content>
        </Tab>
        <Tab
          title="RELATÓRIO DE PRODUÇÕES"
          id={2}
          height="calc(100vh - 130px)"
          checked={activeTabIndex === 2}
          onChange={() => setActiveTabIndex(2)}
        >
          <Content>
            <Form
              ref={formRefChamadas}
              onSubmit={handleDownloadRelatorioChamadas}
              autoComplete="off"
            >
              <div>
                <div className="datas">
                  <h5>Período:</h5>
                  <div style={{ display: 'flex' }}>
                    <DatePicker
                      placeholderText="DE"
                      name="datainicio"
                      disabled={loadingDownload}
                    />
                    <DatePicker
                      placeholderText="ATÉ"
                      name="datafim"
                      disabled={loadingDownload}
                    />
                  </div>
                </div>
                <div>
                  <h5>Setor:</h5>
                  <div>
                    <Radio
                      name="setor"
                      options={[
                        { id: 'hub', label: 'HUB' },
                        { id: 'promo', label: 'PROMO' },
                      ]}
                      disabled={loadingDownload}
                    />
                  </div>
                </div>
              </div>
              <div className="ButtonGroup">
                <Button
                  type="submit"
                  color="primary"
                  disabled={loadingDownload}
                  style={{ width: '20rem' }}
                >
                  {loadingDownload ? (
                    'Gerando o relatório'
                  ) : (
                    <>
                      <FiDownload /> Gerar relatório de Produções
                    </>
                  )}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loadingDownload}
                  style={{ width: '20rem' }}
                  onClick={e => {
                    e.preventDefault();
                    handleDownloadRelatorioSpotRadio();
                  }}
                >
                  {loadingDownload ? (
                    'Gerando o relatório'
                  ) : (
                    <>
                      <FiDownload /> Gerar relatório de Spot Radio
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Content>
          {/* </Card> */}
        </Tab>
        <Tab
          title="RELATÓRIO DE OPTATIVOS LOCAIS"
          id={3}
          height="calc(100vh - 130px)"
          checked={activeTabIndex === 3}
          onChange={() => setActiveTabIndex(3)}
        >
          <Content>
            <Form
              ref={formRefOptativos}
              onSubmit={handleDownloadRelatorioOptativosLocais}
              autoComplete="off"
            >
              <div>
                <div className="datas">
                  <h5>Período:</h5>
                  <div style={{ display: 'flex' }}>
                    <DatePicker
                      placeholderText="DE"
                      name="datainicio"
                      disabled={loadingDownload}
                    />
                    <DatePicker
                      placeholderText="ATÉ"
                      name="datafim"
                      disabled={loadingDownload}
                    />
                  </div>
                  <div className="switchs">
                    <div>
                      <h5>Filtrar Deletados:</h5>
                      <Switch name="filtrarDeletados" />
                    </div>
                    <div>
                      <h5>Filtrar Optativos Sem Mídia:</h5>
                      <Switch name="contemMidia" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="botao">
                <Button
                  type="submit"
                  color="primary"
                  disabled={loadingDownload}
                  style={{ width: '20rem' }}
                >
                  {loadingDownload ? (
                    'Gerando o relatório'
                  ) : (
                    <>
                      <FiDownload /> Gerar relatório
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Content>
        </Tab>
      </Tabs>
    </Container>
  );
}
